import { useEffect, useRef, useState } from 'react';
import { Banknote, CreditCard, MapPin, Phone, Printer, UserRound, Wallet } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import StatusBadge from '../../components/StatusBadge';
import {
  PAY_LATER_ERRORS,
  PAY_LATER_SETTLE_METHODS,
  PAY_LATER_STATUS,
  formatPayLaterAddress,
  mapPayLaterError,
  newClientPaymentId,
  normalizePayLaterItem,
  settlePayLater,
} from '../../api/payLaters';
import { formatCurrency } from '../../data/mockData';
import { payableForTender, payLaterMethodLines, validateSettleAmount } from '../../utils/posMoney';
import { posOfflineQueue, useBrowserOnline } from '../../utils/posOfflineQueue';
import { listLots } from '../../api/inventory';
import { roundMoney } from '../../utils/soldBy';
import form from '../../styles/form.module.css';
import styles from './PayLaterDrawer.module.css';

const METHOD_ICONS = {
  CASH: Banknote,
  UPI: Wallet,
  CARD: CreditCard,
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

function sanitizeMoneyInput(value) {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  const dot = raw.indexOf('.');
  if (dot === -1) return raw;
  return `${raw.slice(0, dot)}.${raw.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`;
}

const STALE_SETTLE_CODES = new Set([
  'already_settled',
  'already_paid',
  'already_voided',
  'not_pay_later',
  'nothing_due',
  'settle_conflict',
]);

export default function PayLaterDrawer({ record, token, onClose, onPaid, onReprint }) {
  const [method, setMethod] = useState('CASH');
  const [collectInput, setCollectInput] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lots, setLots] = useState([]);
  const paymentStamp = useRef({ key: '', id: '', at: '' });
  const online = useBrowserOnline();

  useEffect(() => {
    if (!record?.canEdit || record.errorCode !== 'lot_id_required' || !online || !token) {
      setLots([]);
      return undefined;
    }
    const itemId = (record.items || []).find((line) => !line.lot_id)?.item_id;
    if (!itemId) return undefined;
    let cancelled = false;
    listLots(itemId, token)
      .then(({ lots: next }) => { if (!cancelled) setLots(next || []); })
      .catch(() => { if (!cancelled) setLots([]); });
    return () => { cancelled = true; };
  }, [record, online, token]);

  useEffect(() => {
    if (!record) return;
    setMethod('CASH');
    const pending = record.status === PAY_LATER_STATUS.PENDING;
    const due = roundMoney(pending ? record.outstanding : 0);
    setCollectInput(due > 0 ? String(due) : '');
    setCashReceived('');
    setNote('');
    setError('');
    paymentStamp.current = { key: '', id: '', at: '' };
  }, [record]);

  if (!record) return null;

  const customer = record.customer || {};
  const isPending = record.status === PAY_LATER_STATUS.PENDING;
  const outstanding = roundMoney(isPending ? record.outstanding : 0);
  const amountPaid = roundMoney(record.amountPaid);
  const billTotal = roundMoney(record.total);
  const collectRaw = String(collectInput).trim();
  const settleCheck = collectRaw
    ? validateSettleAmount(collectRaw, outstanding)
    : { ok: false, code: 'invalid_settle_amount', amount: 0 };
  const settleAmount = settleCheck.ok ? settleCheck.amount : 0;
  const remainingDue = settleCheck.ok ? roundMoney(Math.max(0, outstanding - settleAmount)) : outstanding;
  const cashQuote = payableForTender(settleAmount, 'CASH');
  const collectAmount = method === 'CASH' ? cashQuote.payable : settleAmount;
  const tendered = roundMoney(cashReceived);
  const cashEntered = String(cashReceived).trim() !== '' && Number.isFinite(tendered);
  const cashCovers = !cashEntered || tendered + 0.001 >= collectAmount;
  const changeDue = method === 'CASH' && cashEntered && cashCovers
    ? roundMoney(Math.max(0, tendered - collectAmount))
    : 0;
  const methodLines = payLaterMethodLines(record.paidByMethod);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const paymentIdentity = () => {
    const key = `${settleAmount}|${method}`;
    if (paymentStamp.current.key !== key) {
      paymentStamp.current = {
        key,
        id: newClientPaymentId(),
        at: new Date().toISOString(),
      };
    }
    return paymentStamp.current;
  };

  const handleCollect = async (e) => {
    e?.preventDefault?.();
    if (!isPending || submitting) return;
    if (!settleCheck.ok) {
      setError(PAY_LATER_ERRORS[settleCheck.code] || 'Enter the amount to collect.');
      return;
    }
    if (method === 'CASH' && !cashCovers) {
      setError(`Short by ${formatCurrency(collectAmount - tendered)}.`);
      return;
    }
    const stamp = paymentIdentity();
    const fullyPaid = remainingDue <= 0;
    setSubmitting(true);
    setError('');
    try {
      if (!online || !record.orderId) {
        const drawerAmount = method === 'CASH'
          ? payableForTender(settleAmount, 'CASH').payable
          : settleAmount;
        await posOfflineQueue.enqueueSettle({
          clientSaleId: record.clientSaleId,
          orderId: record.orderId || null,
          paymentMethod: method,
          note: note.trim() || undefined,
          clientSettleId: stamp.id,
          settledAt: stamp.at,
          customerPhone: customer.phone,
          amount: settleCheck.omit ? undefined : settleAmount,
          closesBill: fullyPaid,
          billAmount: settleAmount,
          drawerAmount,
        });
        onPaid?.({
          ...record,
          status: fullyPaid ? PAY_LATER_STATUS.COLLECTED : PAY_LATER_STATUS.PENDING,
          localSettlement: fullyPaid,
          waitingSync: true,
          paidVia: method,
          changeDue,
          amountPaid: roundMoney(amountPaid + settleAmount),
          outstanding: fullyPaid ? 0 : remainingDue,
          orderId: record.orderId || null,
        });
        return;
      }
      const data = await settlePayLater(
        record.orderId || record.id,
        {
          payment_method: method,
          amount: settleCheck.omit ? undefined : settleAmount,
          note: note.trim() || undefined,
          client_payment_id: stamp.id,
          client_settle_id: stamp.id,
          settled_at: stamp.at,
        },
        token,
      );
      const sale = data?.sale || {};
      const receipt = data?.receipt || {};
      const updated = normalizePayLaterItem({
        ...record,
        ...sale,
        ...receipt,
        settlement_method: receipt.settlement_method || sale.settlement_method || method,
        settled_at: receipt.settled_at || sale.settled_at || stamp.at,
      });
      onPaid?.({
        ...data,
        ...updated,
        paidVia: method,
        changeDue,
        cashierNote: note.trim() || undefined,
      });
    } catch (err) {
      setError(mapPayLaterError(err));
      if (STALE_SETTLE_CODES.has(err?.code)) onPaid?.(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={Boolean(record)}
      onClose={handleClose}
      title={record.displayId || record.id}
      subtitle={`${formatCurrency(isPending ? outstanding : billTotal)} · ${customer.fullName || 'Customer'}`}
      footer={(
        <div className={styles.footerActions}>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Close
          </Button>
          <Button variant="outline" onClick={() => onReprint?.(record)} disabled={submitting}>
            <Printer size={14} /> Reprint
          </Button>
          {isPending && (
            <Button onClick={handleCollect} disabled={submitting || !settleCheck.ok || (method === 'CASH' && !cashCovers)}>
              {submitting ? 'Saving…' : `Collect ${formatCurrency(collectAmount)} · ${method}`}
            </Button>
          )}
        </div>
      )}
    >
      <div className={styles.metaRow}>
        <StatusBadge status={record.status} size="sm" />
        <span className={styles.metaMuted}>{formatDate(record.createdAt)}</span>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer</h3>
        <p className={styles.detailLine}>
          <UserRound size={14} /> {customer.fullName || '—'}
        </p>
        <p className={styles.detailLine}>
          <Phone size={14} /> {customer.phone ? `+91 ${customer.phone}` : '—'}
        </p>
        <p className={styles.detailLine}>
          <MapPin size={14} /> {formatPayLaterAddress(customer)}
        </p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Sale</h3>
        <div className={styles.kv}>
          <span>Bill total</span>
          <strong>{formatCurrency(billTotal)}</strong>
        </div>
        <div className={styles.kv}>
          <span>Paid</span>
          <strong>{formatCurrency(amountPaid)}</strong>
        </div>
        <div className={styles.kv}>
          <span>{isPending ? 'Still due' : 'Outstanding'}</span>
          <strong>{formatCurrency(isPending ? outstanding : 0)}</strong>
        </div>
        {methodLines.map((line) => (
          <div key={line.method} className={styles.kv}>
            <span>{line.label}</span>
            <span>{formatCurrency(line.amount)}</span>
          </div>
        ))}
        {record.counter && (
          <div className={styles.kv}>
            <span>Counter</span>
            <span>{record.counter}</span>
          </div>
        )}
        {record.status === PAY_LATER_STATUS.COLLECTED && (
          <>
            <div className={styles.kv}>
              <span>Paid via</span>
              <span>{record.paidVia || '—'}</span>
            </div>
            <div className={styles.kv}>
              <span>Paid at</span>
              <span>{formatDate(record.paidAt)}</span>
            </div>
            {record.settlementAmount != null && (
              <div className={styles.kv}>
                <span>Collected</span>
                <span>{formatCurrency(record.settlementAmount)}</span>
              </div>
            )}
            {record.settlementRoundOff != null && Number(record.settlementRoundOff) !== 0 && (
              <div className={styles.kv}>
                <span>Round off</span>
                <span>{formatCurrency(record.settlementRoundOff)}</span>
              </div>
            )}
          </>
        )}
      </section>

      {record.canEdit && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Queued lines</h3>
          {(record.items || []).map((line, index) => (
            <div key={`${line.item_id}-${index}`} className={styles.kv}>
              <span>{line.name || line.item_id}</span>
              <input
                type="number"
                min="0"
                step="0.001"
                aria-label={`Quantity for ${line.name || line.item_id}`}
                defaultValue={line.qty}
                onBlur={(e) => {
                  const qty = Number(e.target.value);
                  if (!Number.isFinite(qty) || qty < 0) return;
                  const items = (record.items || [])
                    .map((entry, entryIndex) => (entryIndex === index ? { ...entry, qty } : entry))
                    .filter((entry) => Number(entry.qty) > 0);
                  posOfflineQueue.updateCharge(record.clientSaleId, { items });
                }}
              />
            </div>
          ))}
          {record.couponCode && (
            <button
              type="button"
              onClick={() => posOfflineQueue.updateCharge(record.clientSaleId, {
                couponCode: null,
                discountAmount: 0,
              })}
            >
              Remove coupon and retry
            </button>
          )}
          {online && lots.length > 0 && (
            <label className={form.field}>
              <span className={form.label}>Lot</span>
              <select
                className={form.input}
                defaultValue=""
                onChange={(e) => {
                  const lotId = e.target.value;
                  if (!lotId) return;
                  const items = (record.items || []).map((line) => (
                    line.lot_id ? line : { ...line, lot_id: lotId, requiresLot: false }
                  ));
                  posOfflineQueue.updateCharge(record.clientSaleId, { items });
                }}
              >
                <option value="">Pick a lot</option>
                {lots.map((lot) => (
                  <option key={lot.lotId} value={lot.lotId}>
                    {formatCurrency(lot.sellingPrice)} · {lot.qty} left
                  </option>
                ))}
              </select>
            </label>
          )}
        </section>
      )}

      {isPending && (
        <form className={styles.settleForm} onSubmit={handleCollect}>
          <h3 className={styles.sectionTitle}>Collect</h3>
          {error && <AuthMessage>{error}</AuthMessage>}
          <p className={styles.settleHint}>
            {formatCurrency(outstanding)} is still due. Enter a smaller amount to collect part of it.
            {!online && ' Offline collection syncs later.'}
          </p>
          {record.syncError && <AuthMessage>{record.syncError}</AuthMessage>}
          <div className={styles.tenderedRow}>
            <label className={form.field}>
              <span className={form.label}>Amount to collect</span>
              <div className={styles.tenderedInput}>
                <span>₹</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Amount toward this bill"
                  aria-label="Amount to collect"
                  value={collectInput}
                  onChange={(e) => {
                    setCollectInput(sanitizeMoneyInput(e.target.value));
                    setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                  }}
                  disabled={submitting}
                />
              </div>
            </label>
            {settleCheck.ok && remainingDue > 0 && (
              <p className={styles.settleHint}>
                {formatCurrency(remainingDue)} will stay due after this payment.
              </p>
            )}
            {!settleCheck.ok && String(collectInput).trim() !== '' && (
              <span className={styles.changeShort}>
                {PAY_LATER_ERRORS[settleCheck.code]}
              </span>
            )}
          </div>
          <div className={styles.methodRow}>
            {PAY_LATER_SETTLE_METHODS.map(({ key, label }) => {
              const Icon = METHOD_ICONS[key] || Wallet;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.methodBtn} ${method === key ? styles.methodActive : ''}`}
                  onClick={() => setMethod(key)}
                  disabled={submitting}
                >
                  <Icon size={15} /> {label}
                </button>
              );
            })}
          </div>
          {method === 'CASH' && (
            <div className={styles.tenderedRow}>
              <label className={form.field}>
                <span className={form.label}>Cash received</span>
                <div className={styles.tenderedInput}>
                  <span>₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="Same as amount, or more for change"
                    aria-label="Cash received"
                    value={cashReceived}
                    onChange={(e) => {
                      setCashReceived(sanitizeMoneyInput(e.target.value));
                      setError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                    }}
                    disabled={submitting}
                  />
                </div>
              </label>
              {cashEntered && (
                <span className={cashCovers ? styles.changeOk : styles.changeShort}>
                  {cashCovers
                    ? `Change to return ${formatCurrency(changeDue)}`
                    : `Short by ${formatCurrency(collectAmount - tendered)}`}
                </span>
              )}
              {cashQuote.round_off !== 0 && (
                <p className={styles.settleHint}>
                  Cash collects {formatCurrency(collectAmount)}
                  {' '}
                  ({cashQuote.round_off > 0 ? '+' : ''}
                  {formatCurrency(cashQuote.round_off)} round off).
                </p>
              )}
            </div>
          )}
          <label className={form.field}>
            <span className={form.label}>Note (optional)</span>
            <input
              className={form.input}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              disabled={submitting}
              placeholder="e.g. collected at counter"
            />
          </label>
        </form>
      )}
    </Drawer>
  );
}
